import React, { useEffect, useState, useMemo, useCallback } from "react"

import { CountryCode, getCountries, getCountryCallingCode } from "libphonenumber-js"
import styled from "styled-components"

import { spacing } from "@izettle/otto"
import { Modal, ModalSize, Radio, RadioGroup, TextField } from "@izettle/react"
import { useLocalization } from "@izettle/tenko-react"

const ModalContainer = styled.div`
  min-height: 328px;
`

const ScrolledContianer = styled.div`
  display: flex;
  height: calc(100% - 78px);
  overflow: scroll;
  margin-top: ${spacing.small};
`

const RadioGroupContainer = styled(RadioGroup)`
  width: 100%;
`

const SearchContainer = styled.div``

type CountryModalProps = {
  isVisible: boolean
  onClose: () => void
  onSelect: (countryCode: string) => void
}

export default function CountryModal({
  isVisible,
  onClose,
  onSelect,
}: CountryModalProps) {
  const { text, isoLocale } = useLocalization()

  const [lang, initCountry] = useMemo(() => {
    const langAndCountry = isoLocale.split("-")
    if (isoLocale === "pt-BR") {
      return [isoLocale, langAndCountry[1]]
    }
    return langAndCountry
  }, [isoLocale])

  const [selectedCountry, setSelectedCountry] = useState(initCountry ?? "US")
  const [search, setSearch] = useState("")
  const [countryNames, setCountryNames] = useState<Record<string, string>>({})

  const listOfCodes = getCountries()

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    import(
      /* webpackChunkName: "country-code" */ `./country-names-locale/${lang}.json`
    ).then((list: Record<string, string>) => {
      setCountryNames(list)
    })
  }, [])

  const handleSelect = (newValue: string) => {
    onSelect(newValue)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    setSelectedCountry(event.currentTarget.value)
    handleSelect(event.currentTarget.value)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value)
  }

  const getOptionLabel = useCallback(
    (code: CountryCode) => `(+${getCountryCallingCode(code)}) ${countryNames[code]}`,
    [countryNames],
  )

  const countryOptions = useMemo(
    () =>
      listOfCodes
        .filter((countryCode) =>
          getOptionLabel(countryCode).toLowerCase().includes(search.toLowerCase()),
        )
        .map((code) => <Radio key={code} title={getOptionLabel(code)} value={code} />),
    [listOfCodes, countryNames, search],
  )

  return (
    <Modal
      id="activation-customer-modal"
      isVisible={isVisible}
      size={ModalSize.Small}
      textCloseModal={text("customer_close")}
      onClose={onClose}
      textLabel={text("signup_select_country_code")}
      style={{ paddingBottom: 0 }}
    >
      <ModalContainer>
        <SearchContainer>
          <TextField
            placeholder={text("signup_search_country_code")}
            onChange={handleSearchChange}
            value={search}
            style={{ minWidth: 0 }}
          />
        </SearchContainer>
        <ScrolledContianer>
          <RadioGroupContainer onChange={handleChange} value={selectedCountry}>
            {countryOptions}
          </RadioGroupContainer>
        </ScrolledContianer>
      </ModalContainer>
    </Modal>
  )
}
